import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorModel, GlobalMessageType, HttpErrorHandler, Priority } from '@spartacus/core';

const OAUTH_ENDPOINT = '/authorizationserver/oauth/token';
const BAD_REQUEST = 400;

@Injectable({
  providedIn: 'root',
})
export class BadRequestHandler extends HttpErrorHandler {
  // responseStatus = HttpResponseStatus.BAD_REQUEST;
  responseStatus = BAD_REQUEST;

  handleError(request: HttpRequest<any>, response: HttpErrorResponse): void {
    this.handleBadPassword(request, response);
    this.handleBadLoginResponse(request, response);
    this.handleBadCartRequest(request, response);
    this.handleValidationError(request, response);
    this.handleCouponInvalidCode(request, response);
    this.handleCouponAlreadyRedeemed(request, response);
    this.handleCouponOrderRecalculationError(request, response);
    this.handleCouponNotActiveExpired(request, response);
    this.handleCouponAlreadyExistsCart(request, response);
    this.handleCouponGeneralError(request, response);
    this.handleAddToCartError(request, response);
    this.handleAddToCartDomainError(request, response);
    this.handleCartNullError(request, response);
    this.handleEntityValidationError(request, response);
    this.handleGetDealerPriceError(request, response);
    this.handleRequestAcctUserExistError(request, response);
  }

  getPriority(): Priority {
    return Priority.HIGH;
  }

  protected handleBadPassword(request: HttpRequest<any>, response: HttpErrorResponse): void {
    if (
      response.url.includes(OAUTH_ENDPOINT) &&
      response.error.error === 'invalid_grant' &&
      request.body.get('grant_type') === 'password'
    ) {
      if (response.error.error_description.includes('disabled')) {
        this.globalMessageService.add(
          {
            key: 'login.userDisabled',
          },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      } else if (response.error.error_description.includes('Bad')) {
        this.globalMessageService.add(
          {
            key: 'login.badRequest',
          },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      } else if (response.error.error_description === 'user_inactive') {
        this.globalMessageService.add(
          {
            key: 'login.userInactive',
          },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      } else {
        this.globalMessageService.add(
          {
            key: 'httpHandlers.badRequestPleaseLoginAgain',
            params: {
              errorMessage: response.error.error_description || response.message || '',
            },
          },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      }
      this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_CONFIRMATION);
    }
  }

  protected handleBadLoginResponse(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'PasswordMismatchError')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'httpHandlers.badRequestOldPasswordIncorrect' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleValidationError(request: HttpRequest<any>, response: HttpErrorResponse): void {
    this.getErrors(response)
      .filter((e) => e.type === 'ValidationError')
      .forEach((error) => {
        if (
          error.subject === 'sessionCart' ||
          error.subject === 'productCode' ||
          error.subject === 'categoryCode' ||
          error.subject === 'code'
        ) {
          this.globalMessageService.add(error.message, GlobalMessageType.MSG_TYPE_ERROR);
        } else if (error.subject === 'csvFile' && error.reason === 'invalid') {
          this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
        } else {
          this.globalMessageService.add(
            {
              key: `httpHandlers.validationErrors.${error.reason}.${error.subject}`,
            },
            GlobalMessageType.MSG_TYPE_ERROR
          );
        }
      });
  }

  protected handleBadCartRequest(request: HttpRequest<any>, response: HttpErrorResponse): void {
    this.getErrors(response)
      .filter((e) => e.subjectType === 'cart' && e.reason === 'notFound')
      .forEach(() => {
        this.globalMessageService.add({ key: 'httpHandlers.cartNotFound' }, GlobalMessageType.MSG_TYPE_ERROR);
      });
  }

  protected handleCouponInvalidCode(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.invalid.code.provided')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'voucher.errors.couponInvalidCodeProvided' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleCouponAlreadyRedeemed(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.already.redeemed')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'voucher.errors.couponAlreadyRedeemed' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleCouponOrderRecalculationError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.order.recalculation.error')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'voucher.errors.couponOrderRecalculationError' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleCouponNotActiveExpired(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.not.active.expired')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'voucher.errors.couponNotActiveExpired' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleCouponAlreadyExistsCart(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.already.exists.cart')
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'voucher.errors.couponAlreadyExistsCart' },
          GlobalMessageType.MSG_TYPE_ERROR
        );
      });
  }

  protected handleCouponGeneralError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'VoucherOperationError' && error.message === 'coupon.general.error')
      .forEach(() => {
        this.globalMessageService.add({ key: 'voucher.errors.couponGeneralError' }, GlobalMessageType.MSG_TYPE_ERROR);
      });
  }

  protected handleAddToCartError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'InsufficientStockError')
      .forEach((error) => {
        sessionStorage.setItem('InsufficientStockError', error.type);
        setTimeout(() => {
          this.globalMessageService.add(error.message, GlobalMessageType.MSG_TYPE_ERROR);
        }, 500);
      });
  }

  protected handleAddToCartDomainError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'DomainError')
      .forEach((error) => {
        this.globalMessageService.add(error.message, GlobalMessageType.MSG_TYPE_ERROR);
      });
  }

  protected handleCartNullError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'NullPointerError' || error.type === 'IllegalArgumentError')
      .forEach((error) => {
        console.error(error);
        this.globalMessageService.add({ key: 'cart.genericError' }, GlobalMessageType.MSG_TYPE_ERROR);
        throw new Error('failed');
      });
  }

  protected handleEntityValidationError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'EntityValidationError')
      .forEach((error) => {
        this.globalMessageService.add(error.message, GlobalMessageType.MSG_TYPE_ERROR);
        throw new Error('failed');
      });
  }

  protected handleGetDealerPriceError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'ShimanoDealerPriceError')
      .forEach((error) => {
        this.globalMessageService.add(error.message, GlobalMessageType.MSG_TYPE_ERROR);
      });
  }

  protected handleRequestAcctUserExistError(request: HttpRequest<any>, response: HttpErrorResponse) {
    this.getErrors(response)
      .filter((error) => error.type === 'DuplicateUidError' && error.message === 'requestAccount.userExist')
      .forEach((error) => {
        this.globalMessageService.add({ key: error.message }, GlobalMessageType.MSG_TYPE_ERROR);
        throw new Error('failed');
      });
  }

  protected getErrors(response: HttpErrorResponse): ErrorModel[] {
    return (response.error.errors || []).filter((error: any) => error.type !== 'JaloObjectNoLongerValidError');
  }
}
